/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React, { useCallback } from 'react';

import { css, jsx } from '@atlaskit/css';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIDefinitionsPlugin } from '../aiDefinitionsPluginType';
import { closeReadingAidsPopup } from '../pm-plugins/ai-definitions/commands';
import type {
	AIDefinitionsPluginSharedState,
	ReadingAidsPopup,
	ReadingAidsPopupPropsInState,
} from '../types';

import { AIDefinitionsGlobalStyles } from './GlobalStyles';
import { READING_AIDS_POPUP_PORTAL_CONTAINER } from './utils/const';
import { useManageSelectionToolbarVisibility } from './utils/hooks';

const readingAidsPopupPortalContainerStyle = css({
	position: 'relative',
});

type CanShowReadingAidsPopup =
	| {
			show: false;
			contentId: undefined;
			ReadingAidsPopup: undefined;
			aiDefinitionsState: undefined;
			isReadingAidsPopupOpen: false;
			readingAidsPopupKey: undefined;
			readingAidsPopupProps: undefined;
	  }
	| {
			show: true;
			// need to add the following properties to make typescript happy when accessing the properties
			contentId: string;
			ReadingAidsPopup: ReadingAidsPopup;
			aiDefinitionsState: AIDefinitionsPluginSharedState;
			isReadingAidsPopupOpen: true;
			readingAidsPopupKey: string;
			readingAidsPopupProps: ReadingAidsPopupPropsInState;
	  };

function canShowReadingAidsPopup({
	props,
	aiDefinitionsState,
}: {
	props: AIDefinitionsProps;
	aiDefinitionsState?: AIDefinitionsPluginSharedState;
}): CanShowReadingAidsPopup {
	const { contentId, editorView, ReadingAidsPopup } = props;
	if (
		editorView &&
		contentId &&
		ReadingAidsPopup &&
		aiDefinitionsState &&
		aiDefinitionsState.isReadingAidsPopupOpen &&
		aiDefinitionsState.readingAidsPopupKey &&
		aiDefinitionsState.readingAidsPopupProps
	) {
		return {
			show: true,
			contentId,
			ReadingAidsPopup,
			aiDefinitionsState,
			isReadingAidsPopupOpen: aiDefinitionsState.isReadingAidsPopupOpen,
			readingAidsPopupKey: aiDefinitionsState.readingAidsPopupKey,
			readingAidsPopupProps: aiDefinitionsState.readingAidsPopupProps,
		};
	}
	return {
		show: false,
		contentId: undefined,
		ReadingAidsPopup: undefined,
		aiDefinitionsState: undefined,
		isReadingAidsPopupOpen: false,
		readingAidsPopupKey: undefined,
		readingAidsPopupProps: undefined,
	};
}

export type AIDefinitionsProps = {
	// api is mandatory but can be undefined
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	editorView: EditorView;
	contentId?: string;
	ReadingAidsPopup?: ReadingAidsPopup;
};

/**
 * readingAidsPopupKey
 * - When there are multiple auto-highlights in the document,
 * - and the user clicks on one auto-highlight to trigger the ReadingAidsPopup, then click another.
 * - readingAidsPopupKey will prevent the first ReadingAidsPopup from switching to the loading state
 * - and let the second ReadingAidsPopup show loading state.
 *
 * - Right now we're hiding the first ReadingAidsPopup when we click the second one.
 * - But readingAidsPopupKey will save us an experiment check if we want to show the second ReadingAidsPopup in the future.
 */
export const AIDefinitions = (props: AIDefinitionsProps) => {
	const { api, editorView } = props;
	const { aiDefinitionsState } = useSharedPluginState(api, ['aiDefinitions']);
	const { editorViewModeState } = useSharedPluginState(api, ['editorViewMode']);

	const defaultOnResult = useCallback(() => {}, []);

	const onLoad = useCallback(() => {
		aiDefinitionsState?.readingAidsPopupProps?.onLoad?.();
		// maybe log analytics event here in the future if needed
	}, [aiDefinitionsState?.readingAidsPopupProps]);

	const defaultOnClose = useCallback(() => {
		closeReadingAidsPopup()(editorView.state, editorView.dispatch);
	}, [editorView]);

	const { show, contentId, ReadingAidsPopup, readingAidsPopupKey, readingAidsPopupProps } =
		canShowReadingAidsPopup({ props, aiDefinitionsState });

	useManageSelectionToolbarVisibility({ api, show });

	if (editorViewModeState?.mode !== 'view') {
		return null;
	}

	return (
		<>
			<AIDefinitionsGlobalStyles />
			<div id={READING_AIDS_POPUP_PORTAL_CONTAINER} css={readingAidsPopupPortalContainerStyle} />
			{show ? (
				<ReadingAidsPopup
					key={readingAidsPopupKey}
					// Ignored via go/ees005
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...readingAidsPopupProps}
					isReadingAidsOpen
					popupPortalContainerId={READING_AIDS_POPUP_PORTAL_CONTAINER}
					contentId={contentId}
					onResult={readingAidsPopupProps.onResult || defaultOnResult}
					onLoad={onLoad}
					onClose={readingAidsPopupProps.onClose || defaultOnClose}
				/>
			) : null}
		</>
	);
};
